import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  ButtonBase,
  Collapse,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';

import { r, color, radius, motion, tabularNums } from '../../../theme/tokens';
import SupplementRow from '../../stack/SupplementRow';
import { RENDER_STATE } from '../renderDecision';
import GeneratingMark from './GeneratingMark';
import InteractionDetailsModal from '../../common/InteractionDetailsModal';
import SupplementProductsPanel from '../../stack/SupplementProductsPanel';
import {
  LOCKED,
  PAUSED,
  STACK_DISPLAY_NAME,
  PIN_ACTION,
  PINNED_ACTION,
  PIN_HELPER,
  PINNED_HELPER,
  overflowLabel,
  SHOW_FEWER,
} from '../copy';

/**
 * One recommended card — §3 anatomy, in the three states §2 can resolve to.
 *
 * The single most important rule this component enforces, and the reason the
 * share controls are absent rather than conditional: **recommended stacks are
 * not shareable, in any state, pinned or not** (§3). There is no
 * Public/Private switch and no share row anywhere in this file, so no future
 * prop can turn one on. Sharing is only for stacks the user built, and those
 * render through `common/StackCard`.
 *
 * The second: **pinning does not move the card** (§5, ruled 2026-08-20). Pin
 * switches the footer from its primary state to its secondary "Pinned" state
 * and changes nothing else — not the row, not the section, not the controls.
 * The card's position is the band's business, and the band never re-sorts.
 *
 * §3's differences from a built card are exactly: the recommended border and
 * the 4px accent bar on the top edge, the "Recommended" badge, the context
 * block, and this footer. Everything else is shared anatomy.
 */

// Defined locally, as `GeneratingMark`, `UndoToast` and `StackCard` each do —
// it is not a token export. Importing it from `tokens` type-checks as
// `undefined` under Jest, which turns the media query into a dead object key
// and passes silently; webpack is the thing that catches it.
const REDUCED = '@media (prefers-reduced-motion: reduce)';

const RECOMMENDED_BADGE = 'Recommended';
const MANAGE = 'Manage ›';
const MAX_ROWS = 4;

const formatDate = (date) => {
  if (!date || !(date instanceof Date) || date.getTime() === 0) return null;
  try {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (error) {
    return null;
  }
};

/**
 * Shared shell: the container §3 describes, in three forms.
 *
 * `paused` is the 4c render's: a quiet card, no accent top rule and no hover
 * lift. The accent rule and the green border say "live recommendation"; a
 * paused card is deliberately the least loud thing in the band, and the render
 * draws it that way.
 */
function CardShell({ locked, paused, children }) {
  const quiet = locked || paused;
  return (
    <Box
      data-testid="recommended-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: radius.lg,
        backgroundColor: locked ? color.surface : color.paper,
        border: locked
          ? `${r(1)} dashed ${color.line}`
          : `${r(1)} solid ${paused ? color.line : color.posBorder}`,
        transition: `border-color ${motion.micro} ${motion.ease}`,
        ...(paused ? {} : { '&:hover': { borderColor: color.accent } }),
        // §10: hairlines separate cards. No shadow here, ever — the two
        // shadows in the system are for the toast and the dialog.
        boxShadow: 'none',
      }}
    >
      {!quiet && (
        <Box
          aria-hidden="true"
          sx={{ height: '0.25rem', backgroundColor: color.accent, flexShrink: 0 }}
        />
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>{children}</Box>
    </Box>
  );
}

/**
 * `showBadge` is false only on the paused card. The 4c render draws no
 * "Recommended" pill there, and it is right not to: "Recommended" and "Paused"
 * on one screen argue with each other, and the band's own badge already says
 * which of the two is true right now.
 */
function TitleRow({ stackType, showBadge = true }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        // Right-aligned, as render 4a draws it: the name holds the left edge
        // and the badge the right, rather than the badge trailing the name.
        justifyContent: 'space-between',
        gap: '0.5rem',
        flexWrap: 'wrap',
      }}
    >
      {/* No icon. Ruled 2026-08-20: other stacks carry none, so these do not
          either — the shield that used to sit here is gone from the surface. */}
      <Box
        component="h3"
        sx={{
          margin: 0,
          fontSize: '1.125rem', // 18px
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: color.ink,
        }}
      >
        {STACK_DISPLAY_NAME[stackType]}
      </Box>
      {showBadge && (
      <Chip
        label={RECOMMENDED_BADGE}
        size="small"
        sx={{
          height: '1.375rem',
          // Brand green under lime, as render 4a draws it (Tanu, 2026-08-27).
          // §3's phrase "accent badge" was read as the literal token first;
          // it means the brand green loosely, and the render wins here.
          //
          // Both values are measured off the render rather than eyeballed.
          // The fill samples `evergreen` exactly. The label samples six
          // channel-units from `posBorder` and seventy-one from `softAccent`,
          // so it is `posBorder` carrying the same transcription drift this
          // handoff already showed once — it wrote the card border a few
          // digits off that same token, which the ticket had to correct. A
          // border-named token carrying text is odd; the measurement is the
          // reason, and it clears AA at 7.07:1. White would have scored
          // higher but is not what the design draws.
          backgroundColor: color.evergreen,
          color: color.posBorder,
          fontSize: '0.6875rem',
          fontWeight: 700,
          letterSpacing: '0.02em',
        }}
      />
      )}
    </Box>
  );
}

/** §3's context block: eyebrow + chips + Manage ›. Full cards only. */
function ContextBlock({ label, items, onManage }) {
  return (
    <Box sx={{ marginTop: '0.875rem' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: '0.6875rem', // 11px eyebrow
            fontWeight: 700,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: color.inkMuted,
          }}
        >
          {label} ({items.length})
        </Box>
        <ButtonBase
          onClick={onManage}
          sx={{
            minHeight: '2.75rem',
            paddingInline: '0.5rem',
            borderRadius: radius.sm,
            color: color.accent,
            fontSize: '0.8125rem',
            fontWeight: 600,
            '&:focus-visible': {
              outline: `${r(2)} solid ${color.accent}`,
              outlineOffset: r(2),
            },
          }}
        >
          {MANAGE}
        </ButtonBase>
      </Box>
      {items.length > 0 && (
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}
        >
          {items.map((item, index) => (
            <Chip
              key={`${item}-${index}`}
              label={item}
              size="small"
              sx={{
                backgroundColor: color.surface,
                border: `${r(1)} solid ${color.line}`,
                color: color.textBody,
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

/**
 * The footer, in its two states. §3: "Unpinned: primary Pin + helper line.
 * Pinned: secondary Pinned + helper line. No share control, ever."
 */
function PinFooter({ isPinned, isPending, onToggle }) {
  return (
    <Box
      sx={{
        marginTop: 'auto',
        padding: '1rem 1.125rem',
        backgroundColor: color.surface,
        borderTop: `${r(1)} solid ${color.line}`,
        display: 'flex',
        // Beside the button, not beneath it (Tanu, 2026-08-26) — render 4a
        // draws it this way. The helper stays on the same line at every
        // breakpoint and wraps its own text rather than dropping below the
        // control, which is the arrangement the ruling rejected.
        flexDirection: 'row',
        alignItems: 'center',
        gap: '0.875rem',
      }}
    >
      <Button
        onClick={onToggle}
        disabled={isPending}
        data-testid="pin-toggle"
        aria-pressed={isPinned}
        sx={{
          flexShrink: 0,
          minHeight: '2.75rem',
          paddingInline: '1.125rem',
          borderRadius: radius.md,
          textTransform: 'none',
          fontWeight: 700,
          fontSize: '0.875rem',
          boxShadow: 'none',
          ...(isPinned
            ? {
                // Secondary: the action is available but no longer the thing
                // being asked for.
                backgroundColor: color.paper,
                color: color.evergreen,
                border: `${r(1)} solid ${color.line}`,
                '&:hover': { backgroundColor: color.surface },
              }
            : {
                backgroundColor: color.accent,
                // Never white on this green — it fails AA at body size.
                color: color.accentInk,
                '&:hover': { backgroundColor: color.accentHover },
              }),
          '&:focus-visible': {
            outline: `${r(2)} solid ${color.accent}`,
            outlineOffset: r(2),
          },
        }}
      >
        {isPinned ? PINNED_ACTION : PIN_ACTION}
      </Button>
      <Box sx={{ flex: 1, minWidth: 0, fontSize: '0.75rem', color: color.inkSoft, lineHeight: 1.5 }}>
        {isPinned ? PINNED_HELPER : PIN_HELPER}
      </Box>
    </Box>
  );
}

/**
 * The accent CTA both the locked and the paused card carry.
 *
 * Extracted rather than copied: the two states differ in what they ask for,
 * not in how the ask looks, and a second hand-maintained copy of this `sx` is
 * how the fill and the `accentInk` pairing drift apart. Never white on this
 * green — it fails AA at body size.
 */
function CtaButton({ children, onClick }) {
  return (
    <Button
      onClick={onClick}
      sx={{
        marginTop: '1rem',
        minHeight: '2.75rem',
        paddingInline: '1.125rem',
        borderRadius: radius.md,
        backgroundColor: color.accent,
        color: color.accentInk,
        fontWeight: 700,
        fontSize: '0.875rem',
        textTransform: 'none',
        boxShadow: 'none',
        '&:hover': { backgroundColor: color.accentHover },
        '&:focus-visible': {
          outline: `${r(2)} solid ${color.accent}`,
          outlineOffset: r(2),
        },
      }}
    >
      {children}
    </Button>
  );
}

/**
 * The ghosted rows behind the blur on a paused card.
 *
 * **These are decoration, not the member's stack, and that is the one place
 * this deliberately departs from a literal reading of the 4c render.**
 *
 * The render blurs the real rows in place. Blur is a CSS filter: it changes
 * how bytes are painted, not whether they are there. Rendering the real names
 * and doses would put them in the DOM of a member whose membership has
 * lapsed — readable by deleting one style rule in devtools, by any script on
 * the page, by a screen reader, and by "view source". This ticket exists to
 * withhold exactly that content, so the literal implementation would defeat
 * the feature it is part of.
 *
 * Fixed placeholder rows give the render's visual — a card with something
 * behind the frosting, not an empty box — while the real values never leave
 * the server-side of the boundary. Blurred, the two are indistinguishable to
 * a viewer; unblurred, only one of them is a leak. The count is three because
 * that is what the render draws, for both cards.
 *
 * They are `aria-hidden` and inert: a screen reader announcing supplement
 * names that are not the member's would be worse than silence.
 *
 * **The text is deliberately generic rather than plausible.** The first draft
 * used real-looking names ("Vitamin B12", "Folate", "Magnesium") to match the
 * render exactly. Writing the test killed it: the fixture stack also contains
 * Vitamin B12, so no assertion could distinguish "ghost decoration" from "the
 * member's data leaked" — and neither could a human reading an unblurred card
 * or a screenshot. Generic text makes the boundary checkable. It also fails
 * safe: if `filter` is ever unsupported or overridden, a lapsed member sees an
 * obvious placeholder instead of three supplements that were never theirs.
 * Under blur the two are the same smear, which is all the render asks for.
 */
const GHOST_ROWS = [
  { name: 'Supplement', dose: '000 mg' },
  { name: 'Supplement', dose: '00 mcg' },
  { name: 'Supplement', dose: '000 mcg' },
];

/**
 * State 4c — the lapsed member, per the render Tanu supplied 2026-08-28.
 *
 * The card keeps its title and description, shows its rows blurred behind a
 * frosted layer, and carries one Reactivate CTA. What it does *not* carry:
 *
 * - **No "Recommended" pill and no accent top rule.** The render draws neither;
 *   the band's "Paused" badge is what states the condition.
 * - **No context block.** The render drops it. This shipped one first because
 *   the ticket's Scope named it explicitly — the render is the later word and
 *   is what was asked for.
 * - **No Pin footer, and no unlock CTA** on a card whose profile input is
 *   missing. §2 says *one* CTA; pinning a stack you cannot read is an action
 *   without a meaning, and two competing asks is what the rule prevents.
 *
 * The blurred rows are `GHOST_ROWS`, never `stack` — see above. This component
 * takes no `stack` prop at all, so there is no supplement data in scope to
 * leak even by accident.
 */
function PausedCard({ stackType, description, onReactivate }) {
  const strings = PAUSED[stackType];

  return (
    <CardShell paused>
      <Box sx={{ padding: '1.25rem 1.125rem 1.25rem' }}>
        <TitleRow stackType={stackType} showBadge={false} />

        {description && (
          <Box
            component="p"
            sx={{
              margin: '0.5rem 0 0',
              fontSize: '0.875rem',
              color: color.inkSoft,
              lineHeight: 1.5,
            }}
          >
            {description}
          </Box>
        )}

        {/* The frosted rows. Inert in every sense: hidden from assistive tech,
            unselectable, and not a click target — there is nothing here to
            interact with, and a user who tries should get no affordance back. */}
        <Box
          aria-hidden="true"
          data-testid="paused-ghost-rows"
          sx={{
            marginTop: '0.875rem',
            paddingTop: '0.875rem',
            borderTop: `${r(1)} solid ${color.line}`,
            filter: 'blur(0.25rem)',
            userSelect: 'none',
            pointerEvents: 'none',
          }}
        >
          {GHOST_ROWS.map((row, index) => (
            <Box
              key={`${row.name}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: '1rem',
                paddingBlock: '0.375rem',
                fontSize: '0.875rem',
                color: color.inkSoft,
              }}
            >
              <span>{row.name}</span>
              <Box component="span" sx={{ fontWeight: 600, ...tabularNums }}>
                {row.dose}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          component="p"
          sx={{
            margin: '0.875rem 0 0',
            fontSize: '0.875rem',
            color: color.inkSoft,
            lineHeight: 1.5,
          }}
        >
          {strings.body}
        </Box>
        <CtaButton onClick={onReactivate}>{strings.cta}</CtaButton>
      </Box>
    </CardShell>
  );
}

/** State 4b — the cold-start path. Dashed shell, unlock CTA, opt-out line. */
function LockedCard({ stackType, description, onUnlock, onHide, isPending }) {
  const strings = LOCKED[stackType];

  return (
    <CardShell locked>
      <Box sx={{ padding: '1.25rem 1.125rem 1rem' }}>
        <TitleRow stackType={stackType} />

        {/* What the stack is *for*, above the hairline — state 4b draws it and
            the build omitted it, so a first-time user was told how to unlock
            something without being told what it was (Tanu, 2026-08-27). Same
            line the populated card carries, so the two states describe the
            stack identically. */}
        {description && (
          <Box
            component="p"
            sx={{
              margin: '0.5rem 0 0',
              fontSize: '0.875rem',
              color: color.inkSoft,
              lineHeight: 1.5,
            }}
          >
            {description}
          </Box>
        )}

        <Box
          component="p"
          sx={{
            marginTop: '0.875rem',
            paddingTop: '0.875rem',
            marginBottom: 0,
            borderTop: `${r(1)} solid ${color.line}`,
            fontSize: '1rem',
            fontWeight: 700,
            color: color.ink,
            lineHeight: 1.35,
          }}
        >
          {strings.heading}
        </Box>
        <Box
          component="p"
          sx={{
            margin: '0.375rem 0 0',
            fontSize: '0.875rem',
            color: color.inkSoft,
            lineHeight: 1.5,
          }}
        >
          {strings.body}
        </Box>
        <CtaButton onClick={onUnlock}>{strings.cta}</CtaButton>
      </Box>

      {/* The opt-out sits beneath a hairline (state 4b) so it reads as an
          escape from the card rather than a second call to action. */}
      <Box
        sx={{
          borderTop: `${r(1)} solid ${color.line}`,
          padding: '0.75rem 1.125rem',
          fontSize: '0.8125rem',
          color: color.inkSoft,
          lineHeight: 1.5,
        }}
      >
        {strings.optOutLead}{' '}
        <ButtonBase
          onClick={onHide}
          disabled={isPending}
          data-testid="hide-for-now"
          sx={{
            color: color.accent,
            fontSize: 'inherit',
            fontWeight: 600,
            textDecoration: 'underline',
            verticalAlign: 'baseline',
            '&:focus-visible': {
              outline: `${r(2)} solid ${color.accent}`,
              outlineOffset: r(2),
            },
          }}
        >
          {strings.optOutLink}
        </ButtonBase>{' '}
        {strings.optOutTail}
      </Box>
    </CardShell>
  );
}

export default function RecommendedStackCard({
  stackType,
  state,
  stack,
  description,
  contextLabel,
  contextItems = [],
  supplementMap = {},
  isPending = false,
  onManage,
  onUnlock,
  onHide,
  onPinToggle,
  // State 4c only. The destination is the reactivation path the dashboard
  // already sent lapsed members to before the rewrite; this ticket restyles
  // the card, it does not touch the flow behind the button.
  onReactivate,
  // §3 lists a "{n} products ›" chip in the recommended row. `productCounts`
  // is a key -> count Map from the same /supplements/find call SupplementsTable
  // already makes; absent or zero simply renders no chip.
  productCounts,
  onSupplementClick,
  // Injected rather than imported: the products API module pulls in axios,
  // which ships ESM that Jest will not transform, and this card is heavily
  // unit-tested. The dashboard passes the real fetcher.
  fetchProducts,
  // §8 keeps recommended cards expanded on mobile, but the design file's own
  // pressure test qualifies it: the cards collapse, and the *first* one opens
  // by default — "never an all-collapsed screen". Tanu asked for the collapse
  // on 2026-08-27; `defaultExpanded` is what keeps the first one open.
  defaultExpanded = true,
  /**
   * The resolved sentence for a check that could not run, or null (STACK-542).
   *
   * Replenish and Nourish say the same thing a My stacks card says when their
   * check fails (Tanu, 2026-09-14), so this card takes the line the same way
   * `StackCard` does — already mapped, from `Dashboard/copy.js`. Before this
   * ticket the card received no check status at all; its risk chips are per
   * row, and a per-row chip cannot report that the whole check was refused.
   */
  interactionFailureLine = null,
}) {
  // §3 keeps the card compact by default; the overflow line expands it to
  // every supplement (Tanu, 2026-08-26). A vegan Nourish stack is nine rows,
  // so the expanded card is tall on mobile — expected, not a layout bug.
  //
  // Declared before the early returns below, not beside the code that uses it.
  // A card changes state in place — locked becomes full when a medication is
  // added, and Undo brings a hidden one back — so a hook after those returns
  // changes the hook count between renders of the same instance and React
  // throws. The suite did not catch it; the dev server's lint did.
  const [showAll, setShowAll] = useState(false);

  // §8: single column and collapsible below 640px, fully expanded from 1024px
  // — the same breakpoint `MyStacksSection` reads for its own accordion.
  const isMobile = useMediaQuery('(max-width:39.9375rem)');
  const [mobileExpanded, setMobileExpanded] = useState(defaultExpanded);
  const [interactionModalOpen, setInteractionModalOpen] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [openProducts, setOpenProducts] = useState(null);

  /**
   * The risk chip opens the same modal it opens on a user-built stack.
   *
   * It rendered but did nothing here (Tanu, 2026-08-27): `SupplementRow` takes
   * an `onRiskClick` and this card never passed one, so the chip was a label
   * wearing a button's affordance — the exact silent shape this surface keeps
   * producing. Shaped like `SupplementsTable`'s per-supplement handler, since
   * the chip here is per row rather than per card.
   */
  const openInteraction = (supplement, displayName) => {
    const interaction = supplement?.interaction;
    if (!interaction?.has_interaction) return;

    const interactions = (interaction.allInteractions || []).map((entry) => ({
      ...entry,
      medicationName:
        entry.medicationName ||
        entry.medication_name ||
        `Medication (${entry.medication_in_rxcui})`,
    }));

    setSelectedInteraction({
      supplementName: displayName,
      interactions,
      primaryInteraction: interaction.primaryInteraction || interaction,
    });
    setInteractionModalOpen(true);
  };

  if (state === RENDER_STATE.NOT_RENDERED) return null;

  // Before the locked branch, and before anything reads `stack`: 4c withholds
  // the same detail whatever the profile resolved to, so a lapsed member gets
  // one paused card per rendered stack rather than a paused card here and an
  // unlock CTA there.
  if (state === RENDER_STATE.PAUSED) {
    return (
      <PausedCard
        stackType={stackType}
        description={description}
        onReactivate={onReactivate}
      />
    );
  }

  if (state === RENDER_STATE.LOCKED) {
    return (
      <LockedCard
        stackType={stackType}
        description={description}
        onUnlock={onUnlock}
        onHide={onHide}
        isPending={isPending}
      />
    );
  }

  const supplements = Array.isArray(stack?.supplements) ? stack.supplements : [];
  const visible = showAll ? supplements : supplements.slice(0, MAX_ROWS);
  const overflow = supplements.length - supplements.slice(0, MAX_ROWS).length;
  const updated = formatDate(stack?.jsUpdatedAt);
  const isGenerating = state === RENDER_STATE.GENERATING;

  return (
    <CardShell>
      <Box sx={{ padding: '1.25rem 1.125rem 0.5rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <TitleRow stackType={stackType} />
          </Box>
          {isMobile && (
            <ButtonBase
              onClick={() => setMobileExpanded((open) => !open)}
              aria-expanded={mobileExpanded}
              aria-label={`${mobileExpanded ? 'Collapse' : 'Expand'} ${
                STACK_DISPLAY_NAME[stackType]
              }`}
              data-testid="recommended-collapse-toggle"
              sx={{
                flexShrink: 0,
                width: '2.75rem', // §8 hit target
                height: '2.75rem',
                borderRadius: radius.sm,
                color: color.inkMuted,
                '&:focus-visible': {
                  outline: `${r(2)} solid ${color.accent}`,
                  outlineOffset: r(2),
                },
              }}
            >
              <ExpandMoreIcon
                sx={{
                  transition: `transform ${motion.ui} ${motion.ease}`,
                  transform: mobileExpanded ? 'rotate(180deg)' : 'none',
                  [REDUCED]: { transition: 'none' },
                }}
              />
            </ButtonBase>
          )}
        </Box>
      </Box>

      {/* §8 keeps these expanded on desktop; below 640px they collapse, with
          the first card open by default (Tanu, 2026-08-27, and the design
          file's own pressure test — "never an all-collapsed screen"). The Pin
          footer sits outside and stays visible in both states, so the primary
          action is never behind a tap. */}
      <Collapse
        in={!isMobile || mobileExpanded}
        timeout={isMobile ? undefined : 0}
        unmountOnExit={false}
      >
        <Box sx={{ padding: '0 1.125rem 0.5rem' }}>
        {description && (
          <Box
            component="p"
            sx={{
              margin: '0.5rem 0 0',
              fontSize: '0.875rem',
              color: color.inkSoft,
              lineHeight: 1.5,
            }}
          >
            {description}
          </Box>
        )}

        {updated && (
          <Box
            sx={{
              marginTop: '0.25rem',
              fontSize: '0.75rem', // 12px italic, per §3
              fontStyle: 'italic',
              color: color.inkMuted,
              ...tabularNums,
            }}
          >
            Updated: {updated}
          </Box>
        )}

        <ContextBlock label={contextLabel} items={contextItems} onManage={onManage} />
      </Box>

      {visible.length > 0 && (
        /* The failure line and the rows it describes, ordered rather than
           duplicated — one element in the DOM, as `StackCard`'s risk row is.
           Above the rows on a phone, below them on desktop, at this card's own
           640px breakpoint rather than a theme one, so it flips with everything
           else the card switches at `isMobile`.

           Both live inside the Collapse because the rows do: a line about rows
           that are not on screen has nothing to describe. That is also why a
           locked or paused card cannot show it — those return their own card
           well before this point, and draw no rows at all. */
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {interactionFailureLine && (
            <Box
              data-testid="recommended-interaction-failure"
              sx={{
                order: isMobile ? 1 : 2,
                display: 'flex',
                alignItems: 'flex-start',
                paddingInline: '1.125rem',
                paddingTop: '0.5rem',
              }}
            >
              <InfoOutlinedIcon
                sx={{
                  fontSize: '1rem',
                  mr: r(6),
                  color: color.inkMuted,
                  mt: '0.0625rem',
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: color.inkMuted,
                  lineHeight: 1.4,
                }}
              >
                {interactionFailureLine}
              </Typography>
            </Box>
          )}
          <Box
            data-testid="recommended-rows"
            sx={{ marginTop: '0.5rem', order: isMobile ? 2 : 1 }}
          >
            {visible.map((supplement, index) => {
              const key = supplement.supplementId || supplement.key;
              const productCount = productCounts?.get?.(key) ?? 0;
              const displayName = supplementMap[key] || supplement.name;
              const row = (
                <SupplementRow
                  key={key || index}
                  supplement={supplement}
                  isFirst={index === 0}
                  readOnly
                  isSelected
                  displayName={displayName}
                  // `note` is what both generators actually write
                  // (`medication_stack.py` asks the model for it;
                  // `diet_stack.py` builds it from `diet_notes`). Neither
                  // `rationale` nor `description` exists on a system stack, so
                  // this line rendered blank in the live app while looking
                  // correct in every fixture that invented one.
                  description={
                    supplement.note || supplement.rationale || supplement.description
                  }
                  showRelevance={false}
                  showProducts={productCount > 0}
                  productCount={productCount}
                  productsExpanded={openProducts === key}
                  onProductsClick={() =>
                    setOpenProducts((open) => (open === key ? null : key))
                  }
                  onRiskClick={() => openInteraction(supplement, displayName)}
                  onNameClick={() => onSupplementClick?.(key)}
                />
              );

              // §3 puts the buying path on the card, so it opens here rather
              // than navigating away (Tanu, 2026-08-27) — the same behaviour a
              // user-built stack has on stack detail.
              return openProducts === key && fetchProducts ? (
                <React.Fragment key={key || index}>
                  {row}
                  <SupplementProductsPanel
                    supplementId={key}
                    supplementName={displayName}
                    fetchProducts={fetchProducts}
                  />
                </React.Fragment>
              ) : (
                row
              );
            })}
            {overflow > 0 && (
              <ButtonBase
                data-testid="recommended-overflow-toggle"
                onClick={() => setShowAll((open) => !open)}
                aria-expanded={showAll}
                sx={{
                  // §8's 44px floor: this is a control now, not a caption.
                  minHeight: '2.75rem',
                  width: '100%',
                  justifyContent: 'flex-start',
                  padding: '0.625rem 1.125rem 0.5rem',
                  fontSize: '0.8125rem', // 13px/600, per §3
                  fontWeight: 600,
                  color: color.inkSoft,
                  textAlign: 'left',
                  '&:hover': { color: color.linkHover },
                  '&:focus-visible': {
                    outline: `${r(2)} solid ${color.accent}`,
                    outlineOffset: r(2),
                  },
                  ...tabularNums,
                }}
              >
                {showAll ? SHOW_FEWER : overflowLabel(overflow)}
              </ButtonBase>
            )}
          </Box>
        </Box>
      )}
      </Collapse>

      {isGenerating ? (
        <Box
          sx={{
            marginTop: 'auto',
            padding: '1rem 1.125rem',
            backgroundColor: color.surface,
            borderTop: `${r(1)} solid ${color.line}`,
          }}
        >
          {/* The mark lives inside the accent CTA slot, so the CTA's own fill
              carries it — that is what makes the mark legible at 1rem. */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: '2.75rem',
              paddingInline: '1.125rem',
              borderRadius: radius.md,
              backgroundColor: color.accent,
            }}
          >
            <GeneratingMark />
          </Box>
        </Box>
      ) : (
        <PinFooter
          isPinned={Boolean(stack?.isPinned)}
          isPending={isPending}
          onToggle={onPinToggle}
        />
      )}

      {/* No disclaimer on the card (Tanu, 2026-09-21 — STACK-541). One per
          card put the same sentence side by side at 1440 and stacked at 390,
          so `RecommendedBand` carries a single line for the pair instead. */}

      <InteractionDetailsModal
        open={interactionModalOpen}
        onClose={() => setInteractionModalOpen(false)}
        supplementName={selectedInteraction?.supplementName}
        interactions={selectedInteraction?.interactions || []}
        primaryInteraction={selectedInteraction?.primaryInteraction}
      />
    </CardShell>
  );
}
